package top.wcpe.allincore.ui.runtime.ir

import top.wcpe.allincore.dsl.ir.model.UiNode
import top.wcpe.allincore.layout.engine.LayoutMode
import top.wcpe.allincore.ui.runtime.element.AutoScroll
import top.wcpe.allincore.ui.runtime.element.Box
import top.wcpe.allincore.ui.runtime.element.Button
import top.wcpe.allincore.ui.runtime.element.CanvasElement
import top.wcpe.allincore.ui.runtime.element.CarouselView
import top.wcpe.allincore.ui.runtime.element.Checkbox
import top.wcpe.allincore.ui.runtime.element.CollapsingHeader
import top.wcpe.allincore.ui.runtime.element.Column
import top.wcpe.allincore.ui.runtime.element.Divider
import top.wcpe.allincore.ui.runtime.element.DragReorder
import top.wcpe.allincore.ui.runtime.element.Dropdown
import top.wcpe.allincore.ui.runtime.element.DropdownOption
import top.wcpe.allincore.ui.runtime.element.EntityPreview
import top.wcpe.allincore.ui.runtime.element.Image
import top.wcpe.allincore.ui.runtime.element.InfiniteScroll
import top.wcpe.allincore.ui.runtime.element.ItemStack
import top.wcpe.allincore.ui.runtime.element.Modal
import top.wcpe.allincore.ui.runtime.element.ModelPreview
import top.wcpe.allincore.ui.runtime.element.MultiColumnVirtualList
import top.wcpe.allincore.ui.runtime.element.PageView
import top.wcpe.allincore.ui.runtime.element.ProgressBar
import top.wcpe.allincore.ui.runtime.element.PullToRefresh
import top.wcpe.allincore.ui.runtime.element.RadioButton
import top.wcpe.allincore.ui.runtime.element.RadioGroup
import top.wcpe.allincore.ui.runtime.element.Row
import top.wcpe.allincore.ui.runtime.element.ScrollView
import top.wcpe.allincore.ui.runtime.element.Slider
import top.wcpe.allincore.ui.runtime.element.Slot
import top.wcpe.allincore.ui.runtime.element.Spacer
import top.wcpe.allincore.ui.runtime.element.Splitter
import top.wcpe.allincore.ui.runtime.element.Switch
import top.wcpe.allincore.ui.runtime.element.Tab
import top.wcpe.allincore.ui.runtime.element.TabView
import top.wcpe.allincore.ui.runtime.element.Text
import top.wcpe.allincore.ui.runtime.element.TextField
import top.wcpe.allincore.ui.runtime.element.Toast
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.element.VirtualGrid
import top.wcpe.allincore.ui.runtime.element.VirtualList
import top.wcpe.allincore.ui.runtime.element.VirtualMasonry
import top.wcpe.allincore.ui.runtime.element.VirtualTree
import top.wcpe.allincore.ui.runtime.element.Zoomable
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import kotlin.jvm.JvmName
import kotlin.jvm.JvmStatic

/**
 * 节点 kind → UIElement 工厂。
 *
 * 已支持的 kind 集合见下方 when；未知 kind 回退到 Box（保留子节点可见性），仅记录警告。
 */
internal object IrElementFactory {

    private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.ir.IrElementFactory")

    /** 根据节点 kind 创建 UI 元素；canvas 节点需要走 [create] 的 kind 重载之外的 canvasData 路径。 */
    @JvmStatic
    @JvmName("create")
    fun create(node: UiNode): UIElement<*> {
        if ("canvas" == node.kind) {
            val canvasData = node.canvasData
            if (canvasData == null) {
                LOGGER.warn("canvas 节点 ${node.id} 缺少 canvasData，回退到空 canvas")
                return CanvasElement.empty()
            }
            return CanvasElement.create(canvasData)
        }
        return create(node.kind)
    }

    @JvmStatic
    @JvmName("create")
    fun create(kind: String): UIElement<*> = when (kind) {
        "box" -> Box.create()
        "stack" -> Box.create().apply { style().layout().mode = LayoutMode.STACK }
        "grid" -> Box.create().apply { style().layout().mode = LayoutMode.GRID }
        "virtualList" -> VirtualList.create()
        "virtualGrid" -> VirtualGrid.create()
        "virtualMasonry" -> VirtualMasonry.create()
        "virtualTree" -> VirtualTree.create()
        "multiColumnList" -> MultiColumnVirtualList.create()
        "pageview" -> PageView.create()
        "carousel" -> CarouselView.create()
        "pullToRefresh" -> PullToRefresh.create()
        "infiniteScroll" -> InfiniteScroll.create()
        "collapsingHeader" -> CollapsingHeader.create()
        "zoomable" -> Zoomable.create()
        "autoScroll" -> AutoScroll.create()
        "dragReorder" -> DragReorder.create()
        "text" -> Text.create()
        "button" -> Button.create()
        "row" -> Row.create()
        "column" -> Column.create()
        "spacer" -> Spacer.create()
        "checkbox" -> Checkbox.create()
        "progressbar" -> ProgressBar.create()
        "image" -> Image.create()
        "divider" -> Divider.create()
        "splitter" -> Splitter.create()
        "slider" -> Slider.create()
        "textfield" -> TextField.create()
        "switch" -> Switch.create()
        "radio" -> RadioButton.create()
        "radiogroup" -> RadioGroup.create()
        "dropdown" -> Dropdown.create()
        "option" -> DropdownOption.create()
        "scrollview" -> ScrollView.create()
        "tabview" -> TabView.create()
        "tab" -> Tab.create()
        "itemstack" -> ItemStack.create()
        "slot" -> Slot.create()
        "entitypreview", "entityPreview" -> EntityPreview.create()
        "modelpreview", "modelPreview" -> ModelPreview.create()
        "modal" -> Modal.create()
        "toast" -> Toast.create()
        // 菜单 overlay 根节点按列排列子项
        "rightClickMenu", "contextMenu" -> Column.create()
        // 菜单项按 Button 渲染（支持 text + onClick + hover）
        "item" -> Button.create()
        else -> {
            LOGGER.warn("未知元素 kind: $kind，回退到 Box")
            Box.create()
        }
    }
}
